import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';
import { getActions, getGlobal, getPromiseActions, withGlobal } from '../../global';

import type { AuthType, EnclaveSession, MigrationErrorPresentation } from '../../global/types';

import {
  PIN_LENGTH,
  SUPPORT_USERNAME,
  WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION,
} from '../../config';
import { presentAuthorizeFailure } from '../../global/helpers/authorizeFailure';
import {
  selectHasLegacyBiometrics,
  selectIsBiometricAuthEnabled,
  selectLegacyAuthConfig,
  selectShouldMigrate,
} from '../../global/selectors';
import { selectAuthUsageCountRequest, selectEnclaveToken } from '../../global/selectors/enclave';
import { getDoesUsePinPad, getIsFaceIdAvailable, getIsTouchIdAvailable } from '../../util/biometrics';
import buildClassName from '../../util/buildClassName';
import captureKeyboardListeners from '../../util/captureKeyboardListeners';
import { stopEvent } from '../../util/domEvents';
import { getTranslation } from '../../util/langProvider';
import { pause, withTimeout } from '../../util/schedulers';
import { createSignal } from '../../util/signals';
import { enclave, type LegacyAuthConfig } from '../../enclave';
import { getEnclaveErrorCode } from '../../enclave/errors';
import { ANIMATED_STICKERS_PATHS } from './helpers/animatedAssets';

import { useDeviceScreen } from '../../hooks/useDeviceScreen';
import useEffectOnce from '../../hooks/useEffectOnce';
import useFlag from '../../hooks/useFlag';
import useFocusAfterAnimation from '../../hooks/useFocusAfterAnimation';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { useMatchCount } from '../../hooks/useMatchCount';
import useSyncEffect from '../../hooks/useSyncEffect';
import useTimeout from '../../hooks/useTimeout';
import useToggleClass from '../../hooks/useToggleClass';

import LogOutModal from '../main/modals/LogOutModal';
import AnimatedIconWithPreview from './AnimatedIconWithPreview';
import Button from './Button';
import Input from './Input';
import PinPad from './PinPad';
import Transition from './Transition';

import modalStyles from './Modal.module.scss';
import styles from './PasswordForm.module.scss';

type OperationType = 'transfer' | 'sending' | 'staking' | 'unstaking' | 'swap'
  | 'unfreeze' | 'passcode' | 'unlock' | 'claim' | 'turnOnBiometrics' | 'mintCard';

interface OwnProps {
  isActive: boolean;
  isLoading?: boolean;
  operationType?: OperationType;
  /** Lang key of the title above the pinpad. Defaults to the title derived from `operationType`. */
  operationTitle?: string;
  cancelLabel?: string;
  submitLabel?: string | TeactNode[];
  stickerSize?: number;
  placeholder?: string;
  error?: string;
  pinPadTitle?: string;
  help?: string;
  resetStateDelayMs?: number;
  containerClassName?: string;
  pinPadClassName?: string;
  withCloseButton?: boolean;
  isFullWidthButton?: boolean;
  children?: TeactNode;
  noAnimatedIcon?: boolean;
  inputWrapperClassName?: string;
  noBiometrics?: boolean;
  errorClassName?: string;
  noAutoConfirm?: boolean;
  footerNotice?: TeactNode;
  /**
   * Secret reads this flow needs beyond the single one every operation gets. A usage is spent when
   * the private key is read, not when an API call is made, so only a flow that reads the key in more
   * than one step has to ask for more; the session dies on the last read it granted. A flow that has
   * to name the largest count it might need gives back what it did not spend when it ends.
   */
  extraAuthUsages?: number;
  onCancel?: NoneToVoidFunction;
  /** The pinpad close button, when closing differs from the cancel button, such as a step back. Defaults to `onCancel`. */
  onClose?: NoneToVoidFunction;
  onUpdate?: NoneToVoidFunction;
  onAuthorize: (enclaveToken: string) => void;
  onError?: (error?: string) => void;
}

interface StateProps {
  isPasswordNumeric?: boolean;
  isAutoConfirmEnabled: boolean;
  enclaveSessionValidUntil?: number;
  isBiometricAuthEnabled: boolean;
  shouldMigrate?: boolean;
  hasLegacyBiometrics?: boolean;
  legacyAuthConfig?: LegacyAuthConfig;
  authUsageCountRequest?: number;
  multichainUpgradeAccountIds?: string[];
  isAppLocked?: boolean;
}

const STICKER_SIZE = 180;
const APPEAR_ANIMATION_DURATION_MS = 300;
const OTHER_AUTH_CHECK_TIMEOUT = 3000;

const [getHandleBiometricsSignal, setHandleBiometricsSignal] = createSignal(Date.now());

export function triggerPasswordFormHandleBiometrics(e?: MouseEvent | KeyboardEvent) {
  if (e) {
    stopEvent(e);
  }
  setHandleBiometricsSignal(Date.now());
}

function useMigrationFailureDialog(operationType?: OperationType) {
  const { showDialog } = getActions();

  return useLastCallback((titleKey: string, messageKey: string, errorCode?: string) => {
    showDialog({
      title: titleKey,
      message: getTranslation(messageKey, {
        support_link: (
          <a href={`https://t.me/${SUPPORT_USERNAME}`} target="_blank" rel="noreferrer">
            @{SUPPORT_USERNAME}
          </a>
        ),
        error_code: errorCode,
      }),
      noBackdropClose: true,
      isInAppLock: operationType === 'unlock',
      // The logs are what tells support what happened on the device
      buttons: { confirm: { action: 'exportLogs' }, cancel: { title: 'Close' } },
    });
  });
}

function PasswordForm({
  isActive,
  isLoading,
  operationType,
  operationTitle,
  isPasswordNumeric,
  isBiometricAuthEnabled: isBiometricAuthEnabledProp,
  cancelLabel,
  submitLabel,
  stickerSize = STICKER_SIZE,
  placeholder = getDoesUsePinPad() ? 'Enter your passcode' : 'Enter your password',
  error,
  pinPadTitle,
  help,
  resetStateDelayMs,
  containerClassName,
  pinPadClassName,
  children,
  withCloseButton,
  isFullWidthButton,
  noAnimatedIcon,
  inputWrapperClassName,
  noBiometrics,
  errorClassName,
  isAutoConfirmEnabled,
  enclaveSessionValidUntil,
  noAutoConfirm,
  footerNotice,
  shouldMigrate,
  hasLegacyBiometrics,
  legacyAuthConfig,
  authUsageCountRequest,
  multichainUpgradeAccountIds,
  isAppLocked,
  extraAuthUsages,
  onUpdate,
  onCancel,
  onClose,
  onAuthorize,
  onError,
}: OwnProps & StateProps) {
  const {
    setEnclaveSession,
    migrateLegacyAuth,
    migrateLegacyBiometricAuth,
    upgradeMultichainAccounts,
    releaseEnclaveSession,
  } = getActions();

  const lang = useLang();

  const isBiometricAuthEnabled = isBiometricAuthEnabledProp && !noBiometrics;

  const inputRef = useRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState<string>('');
  const [localError, setLocalError] = useState<string>('');
  const { isSmallHeight, isPortrait } = useDeviceScreen();
  const withAutoConfirm = useCanAutoConfirm(enclaveSessionValidUntil, noAutoConfirm);
  /**
   * Whether a passcode or biometric entry on this screen opens the auto-confirm window. This is separate from
   * `noAutoConfirm`, which only decides whether the screen may be skipped while the window is open.
   *
   * The screen that turns biometrics on never opens the window. `enableBiometrics` replaces the session
   * minted here with a single-use biometric one, so the window would be dropped right away.
   */
  const isLongSession = isAutoConfirmEnabled && operationType !== 'turnOnBiometrics';
  const isSubmitDisabled = !inputValue.length && !withAutoConfirm;
  const canUsePinPad = getDoesUsePinPad();
  const [isLogOutModalOpened, openLogOutModal, closeLogOutModal] = useFlag(false);
  // The biometric screen offers its retry only while something failed, and the dialog paths clear the
  // inline error the retry used to be keyed on, which would leave that screen without a single control
  const [hasMigrationFailed, markMigrationFailed, clearMigrationFailure] = useFlag(false);
  // Once the storage is known to have lost the data, no number of attempts gets past it
  const [isAuthDataMissing, markAuthDataMissing, clearAuthDataMissing] = useFlag(false);
  const shouldSuggestLogout = useMatchCount(!!error || !!localError, WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION)
    || isAuthDataMissing;
  const showMigrationFailureDialog = useMigrationFailureDialog(operationType);
  const isAuthorizingRef = useRef(false);
  // Set while the password or biometrics is being checked. The Back and close buttons wait for the check to end:
  // if the user could leave earlier, the late result might confirm whatever the screen shows by then.
  const [isVerifying, markVerifying, unmarkVerifying] = useFlag(false);

  /**
   * Every authorization path reports through here, so a diagnosis that needs the dialog cannot end
   * up in the inline error slot on one path while getting the dialog on the other. The inline slot is
   * a single line under the input, which would leave the "do not reinstall" part of the answer unsaid.
   */
  const handleAuthError = useLastCallback((presentation: MigrationErrorPresentation) => {
    isAuthorizingRef.current = false;

    if (presentation.kind === 'inline') {
      setLocalError(presentation.text);
      onError?.(presentation.text);
      return;
    }

    // An earlier attempt may have left "Wrong password" under the input, and leaving it there next to
    // an answer that says the password is not the problem contradicts what is being said. It is
    // cleared on both sides, because the parent owns a slot of its own that outranks the local one.
    setLocalError('');
    onError?.(undefined);
    markMigrationFailed();

    // A dismissed prompt is not a failure to report. The mark above is what puts the retry control
    // back on the biometric screen, which is all this path owes the user.
    if (presentation.kind === 'silent') return;

    // The pinpad holds its digits until the value is cleared, so a full pinpad behind the dialog
    // reads as a frozen screen
    setInputValue('');
    showMigrationFailureDialog(presentation.titleKey, presentation.messageKey, presentation.errorCode);
  });

  const handleAuthorizeFailure = useLastCallback((
    err: unknown, authType: AuthType, mayOtherAuthUnlock: boolean,
  ) => {
    const presentation = presentAuthorizeFailure(err, authType, getGlobal().authTypes, mayOtherAuthUnlock);
    // An authorization answers with a dialog only when the storage has lost the data it checks against
    if (presentation.kind === 'dialog') {
      markAuthDataMissing();
    }

    handleAuthError(presentation);
  });

  // One usage for the operation itself plus the extra usages it asked for
  const operationUsageCount = 1 + (extraAuthUsages ?? 0);
  const extraUsages = (authUsageCountRequest ?? 0) + (extraAuthUsages ?? 0);
  const usageCount = extraUsages ? 1 + extraUsages : undefined;

  // `upgradeAccountIds` are the accounts `usageCount` budgeted for. The global list may change while the passcode
  // is checked, and an upgrade run on accounts outside the budget spends the reads the operation itself needs.
  const handleAuthorized = useLastCallback((enclaveToken: string, upgradeAccountIds?: string[]) => {
    if (upgradeAccountIds?.length) {
      upgradeMultichainAccounts({ enclaveToken, accountIds: upgradeAccountIds });
    }
    onAuthorize(enclaveToken);
  });

  // Grows each time the form is shown, hidden or removed. A check remembers the value when it starts. If the value has
  // changed by the time the check ends, the user has left the form, and the result is ignored.
  //
  // A form that is never shown or hidden during a check, such as the lock screen after a manual lock, works as usual.
  const visibilityChangeCountRef = useRef(0);

  useSyncEffect(() => {
    visibilityChangeCountRef.current++;
  }, [isActive]);

  useEffectOnce(() => () => {
    visibilityChangeCountRef.current++;
  });

  // Starts a password or biometric check. Call the returned function when the check ends. It returns `false` if
  // the user has left the form in the meantime.
  //
  // Such a late result is ignored completely, even an error. It must not touch the state of a newer check that may
  // be running by then.
  const startCheck = useLastCallback(() => {
    const visibilityChangeCount = visibilityChangeCountRef.current;
    markVerifying();

    return () => {
      const isCurrent = visibilityChangeCount === visibilityChangeCountRef.current;
      if (isCurrent) unmarkVerifying();

      return isCurrent;
    };
  });

  useEffect(() => {
    if (isActive) {
      setLocalError('');
      setInputValue('');
      clearMigrationFailure();
      clearAuthDataMissing();
      isAuthorizingRef.current = false;
      unmarkVerifying();
    }
  }, [isActive]);

  const handleSubmit = useLastCallback(async (pin?: string) => {
    // Prevent double authorization (e.g., race between PIN input and biometrics)
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;

    if (withAutoConfirm) {
      handleAuthorized(selectEnclaveToken(getGlobal())!, multichainUpgradeAccountIds);
      return;
    }

    const password = pin ?? inputValue;

    // Migration from legacy auth to Enclave
    if (shouldMigrate) {
      const endCheck = startCheck();
      migrateLegacyAuth({
        password,
        isLongSession,
        // A migration hands its session straight to the operation and never starts the multichain
        // upgrade, so budgeting for the upgrade would leave those reads unspent - and unspent means
        // available for good, since a counted session has no expiry. Turning biometrics back on does
        // read the key, and that read comes out of this same session.
        usageCount: operationUsageCount + (hasLegacyBiometrics ? 1 : 0),
        onSuccess: async (token) => {
          // The migration has already replaced the legacy auth, so biometrics are turned back on even if the user
          // has left the form
          const biometricsSetup = hasLegacyBiometrics ? getPromiseActions().enableBiometrics({}) : undefined;

          if (endCheck()) {
            onAuthorize(token);
            return;
          }

          // The biometrics setup reads the key through this session, so the session is released after the setup ends
          await biometricsSetup;
          releaseEnclaveSession({ enclaveToken: token });
        },
        onError: (presentation) => {
          if (!endCheck()) return;

          handleAuthError(presentation);
        },
      });
      return;
    }

    // Normal authorization
    const endCheck = startCheck();

    let enclaveSession: EnclaveSession;
    try {
      enclaveSession = await enclave.authorizeOrThrow('passcode', isLongSession, password, usageCount);
    } catch (err) {
      const mayOtherAuthUnlock = await checkMayOtherAuthUnlock(err, 'passcode');
      if (!endCheck()) return;

      handleAuthorizeFailure(err, 'passcode', mayOtherAuthUnlock);
      return;
    }

    if (!endCheck()) {
      releaseEnclaveSession({ enclaveToken: enclaveSession.token });
      return;
    }

    setEnclaveSession(enclaveSession);
    handleAuthorized(enclaveSession.token, multichainUpgradeAccountIds);
  });

  const handleBiometrics = useLastCallback(async () => {
    // Prevent double authorization (e.g., race between PIN input and biometrics)
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;

    setLocalError('');
    const endCheck = startCheck();

    let enclaveSession: EnclaveSession;
    try {
      enclaveSession = await enclave.authorizeOrThrow('biometric', isLongSession, undefined, usageCount);
    } catch (err) {
      const mayOtherAuthUnlock = await checkMayOtherAuthUnlock(err, 'biometric');
      if (!endCheck()) return;

      handleAuthorizeFailure(err, 'biometric', mayOtherAuthUnlock);
      return;
    }

    if (!endCheck()) {
      releaseEnclaveSession({ enclaveToken: enclaveSession.token });
      return;
    }

    setEnclaveSession(enclaveSession);
    handleAuthorized(enclaveSession.token, multichainUpgradeAccountIds);
  });

  // Handle legacy biometrics migration
  const handleLegacyBiometricsMigration = useLastCallback(() => {
    if (!legacyAuthConfig || legacyAuthConfig.kind === 'password') {
      return;
    }

    // Prevent double authorization
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;

    setLocalError('');
    clearMigrationFailure();
    const endCheck = startCheck();

    migrateLegacyBiometricAuth({
      legacyAuthConfig,
      isLongSession,
      // The upgrade is left out for the same reason as in the passcode migration above
      usageCount: operationUsageCount,
      onSuccess: (token) => {
        if (!endCheck()) {
          releaseEnclaveSession({ enclaveToken: token });
          return;
        }

        onAuthorize(token);
      },
      onError: (presentation) => {
        if (!endCheck()) return;

        handleAuthError(presentation);
      },
    });
  });

  // Legacy biometrics while the migration is pending, Enclave biometrics after it
  const handleBiometricsRequest = useLastCallback(() => {
    if (shouldMigrate && hasLegacyBiometrics) {
      handleLegacyBiometricsMigration();
    } else if (!shouldMigrate) {
      void handleBiometrics();
    }
  });

  useEffect(() => {
    if (!isActive || !isBiometricAuthEnabled || withAutoConfirm) {
      return undefined;
    }

    // The form may be hidden again before the appear animation ends, and a prompt on a hidden form
    // would authorize whatever that form was asked for
    let isCancelled = false;
    void pause(APPEAR_ANIMATION_DURATION_MS).then(() => {
      if (!isCancelled) handleBiometricsRequest();
    });

    return () => {
      isCancelled = true;
    };
  }, [isActive, isBiometricAuthEnabled, withAutoConfirm, shouldMigrate, hasLegacyBiometrics, legacyAuthConfig]);

  useEffectOnce(() => {
    // The lock screen fires this signal into every mounted form, and only the unlock form may answer it
    if (operationType !== 'unlock') return undefined;

    return getHandleBiometricsSignal.subscribe(handleBiometricsRequest);
  });

  useFocusAfterAnimation(inputRef, !isActive || isBiometricAuthEnabled);

  useToggleClass({ className: 'is-password-form-visible', isActive });

  const handleClearError = useLastCallback(() => {
    setLocalError('');
    clearMigrationFailure();
    onUpdate?.();
    onError?.(undefined);
  });

  const handleInput = useLastCallback((value: string) => {
    setInputValue(value);
    handleClearError();
  });

  const handleOpenLogOutModal = useLastCallback((e: React.MouseEvent) => {
    stopEvent(e);
    openLogOutModal();
  });

  // Under the lock screen only the unlock form takes Enter; another form with a typed password would sign its request
  useEffect(() => {
    return isSubmitDisabled || isLoading || (isAppLocked && operationType !== 'unlock')
      ? undefined
      : captureKeyboardListeners({ onEnter: () => handleSubmit() });
  }, [handleSubmit, isLoading, isSubmitDisabled, isAppLocked, operationType]);

  function getPinPadTitle() {
    switch (operationType) {
      case 'unfreeze':
        return 'Confirm Unfreezing';
      case 'passcode':
      case 'turnOnBiometrics':
        return 'Confirm Passcode';
      case 'transfer':
      case 'sending':
        return 'Confirm Sending';
      case 'staking':
        return 'Confirm Staking';
      case 'unstaking':
        return 'Confirm Unstaking';
      case 'swap':
        return 'Confirm Swap';
      case 'unlock':
        return undefined;
      case 'claim':
        return 'Confirm Rewards Claim';
      case 'mintCard':
        return 'Confirm Upgrading';
      default:
        return 'Confirm Action';
    }
  }

  const shouldRenderFullWidthButton = operationType === 'unlock';
  const hasSubmitButton = !isBiometricAuthEnabled || withAutoConfirm;
  const footerButtonsClassName = buildClassName(
    modalStyles.footerButtons,
    shouldRenderFullWidthButton && modalStyles.footerButtonFullWidth,
  );

  function renderFooterNotice() {
    if (!footerNotice) {
      return undefined;
    }

    return (
      <div className={styles.footerNotice}>{footerNotice}</div>
    );
  }

  function renderFooterButtons() {
    return (
      <div className={footerButtonsClassName}>
        {onCancel && (
          <Button
            isLoading={isLoading && !hasSubmitButton}
            isDisabled={(isLoading && hasSubmitButton) || isVerifying}
            className={modalStyles.buttonHalfWidth}
            onClick={onCancel}
          >
            {cancelLabel || lang('Cancel')}
          </Button>
        )}
        {isBiometricAuthEnabled && (Boolean(localError) || hasMigrationFailed) && (
          <Button
            isPrimary
            isLoading={isLoading}
            isDisabled={isLoading}
            className={buildClassName(modalStyles.buttonHalfWidth, styles.retryButton)}
            onClick={!isLoading ? handleBiometricsRequest : undefined}
            shouldStopPropagation
          >
            {lang('Try Again')}
          </Button>
        )}
        {hasSubmitButton && (
          <Button
            isPrimary
            isLoading={isLoading}
            isDisabled={isSubmitDisabled}
            className={(shouldRenderFullWidthButton || isFullWidthButton)
              ? modalStyles.buttonFullWidth : modalStyles.buttonHalfWidth}
            onClick={!isLoading ? handleSubmit : undefined}
          >
            {submitLabel || lang('Send')}
          </Button>
        )}
      </div>
    );
  }

  if (canUsePinPad) {
    const hasError = Boolean(localError || error);
    const title = operationTitle ?? getPinPadTitle();
    const actionName = lang(
      !isBiometricAuthEnabled
        ? 'Enter code'
        : getIsFaceIdAvailable()
          ? 'Enter code or use Face ID'
          : getIsTouchIdAvailable()
            ? 'Enter code or use Touch ID'
            : 'Enter code or use biometrics',
    );

    const content = (
      <>
        {withCloseButton && (
          <Button
            isRound
            isDisabled={isVerifying}
            className={buildClassName(modalStyles.closeButton, styles.closeButton)}
            ariaLabel={lang('Close')}
            onClick={onClose || onCancel}
          >
            <i className={buildClassName(modalStyles.closeIcon, 'icon-close')} aria-hidden />
          </Button>
        )}
        <div className={styles.pinPadHeader}>
          {isPortrait && !noAnimatedIcon && (
            <AnimatedIconWithPreview
              play={isActive}
              tgsUrl={ANIMATED_STICKERS_PATHS.guard}
              previewUrl={ANIMATED_STICKERS_PATHS.guardPreview}
              noLoop={false}
              nonInteractive
            />
          )}
          {!isSmallHeight && title && <div className={styles.title}>{lang(title)}</div>}
          {children}
        </div>

        {renderFooterNotice()}
        {withAutoConfirm ? renderFooterButtons() : (
          <PinPad
            isActive={isActive}
            title={lang(hasError
              ? (localError || error!)
              : (pinPadTitle || (isSmallHeight && title ? title : actionName)),
            )}
            type={hasError ? 'error' : undefined}
            length={PIN_LENGTH}
            resetStateDelayMs={resetStateDelayMs}
            value={inputValue}
            className={pinPadClassName}
            onBiometricsClick={isBiometricAuthEnabled ? handleBiometricsRequest : undefined}
            onLogOutClick={operationType === 'unlock' ? openLogOutModal : undefined}
            isLogOutSuggested={isAuthDataMissing}
            onChange={setInputValue}
            onClearError={handleClearError}
            onSubmit={handleSubmit}
          />
        )}
        {operationType === 'unlock' && (
          <LogOutModal isOpen={isLogOutModalOpened} onClose={closeLogOutModal} isInAppLock />
        )}
      </>
    );

    return withAutoConfirm ? (
      <div className={modalStyles.transitionContent}>
        {content}
      </div>
    ) : content;
  }

  function renderBiometricPrompt() {
    const renderingError = localError || error;

    return (
      <Transition name="fade" activeKey={renderingError ? 1 : 0} className={styles.biometricPrompt}>
        {renderingError ? (
          <div className={styles.error}>{lang(renderingError)}</div>
        ) : (
          <div className={styles.verify}>
            {lang(operationType === 'transfer'
              ? 'Please confirm transfer using biometrics' : 'Please confirm action using biometrics')}
          </div>
        )}
      </Transition>
    );
  }

  function renderPasswordForm() {
    return (
      <>
        <Input
          ref={inputRef}
          type="password"
          isRequired
          id="first-password"
          wrapperClassName={inputWrapperClassName}
          errorClassName={errorClassName}
          inputMode={isPasswordNumeric ? 'numeric' : undefined}
          error={error ? lang(error) : localError}
          placeholder={lang(placeholder)}
          value={inputValue}
          onInput={handleInput}
          maxLength={isPasswordNumeric ? PIN_LENGTH : undefined}
        />
        {help && !error && (
          <div className={styles.label}>{help}</div>
        )}
      </>
    );
  }

  return (
    <div className={buildClassName(modalStyles.transitionContent, containerClassName)}>
      {!noAnimatedIcon && (
        <AnimatedIconWithPreview
          tgsUrl={ANIMATED_STICKERS_PATHS.holdTon}
          previewUrl={ANIMATED_STICKERS_PATHS.holdTonPreview}
          play={isActive}
          size={stickerSize}
          nonInteractive
          noLoop={false}
          className={styles.sticker}
        />
      )}

      {children}

      {!withAutoConfirm && (isBiometricAuthEnabled ? renderBiometricPrompt() : renderPasswordForm())}

      {operationType === 'unlock' && (
        <div className={buildClassName(styles.logOutWrapper, !shouldSuggestLogout && styles.logOutWrapperHidden)}>
          {lang('Can\'t confirm?')}
          <span
            role="button"
            tabIndex={0}
            className={styles.logOutButton}
            onClick={handleOpenLogOutModal}
          >
            {lang('Exit all wallets')}
            <i className={buildClassName('icon-chevron-right', styles.detailsIcon)} aria-hidden />
          </span>
        </div>
      )}

      {renderFooterNotice()}
      {renderFooterButtons()}

      {operationType === 'unlock' && (
        <LogOutModal isOpen={isLogOutModalOpened} onClose={closeLogOutModal} isInAppLock />
      )}
    </div>
  );
}

// The recorded auth types are saved seconds after the enclave swaps a method, so a crash in between leaves them
// naming a method the enclave dropped while the key is still there under the other one
async function checkMayOtherAuthUnlock(err: unknown, authType: AuthType) {
  if (getEnclaveErrorCode(err) !== 'auth_not_configured') return false;

  // A check that fails or hangs has not shown the key gone, and calling the data lost sends the user to log out
  const otherAuthType = authType === 'passcode' ? 'biometric' : 'passcode';
  return withTimeout(enclave.isAuthProvisioned(otherAuthType), OTHER_AUTH_CHECK_TIMEOUT, true);
}

function useCanAutoConfirm(enclaveSessionValidUntil?: number, isDisabled?: boolean) {
  const autoConfirmTtl = enclaveSessionValidUntil ? enclaveSessionValidUntil - Date.now() : undefined;
  const [canAutoConfirm, setCanAutoConfirm] = useState(
    autoConfirmTtl !== undefined && autoConfirmTtl > 0,
  );

  useEffect(() => {
    const currentTtl = enclaveSessionValidUntil ? enclaveSessionValidUntil - Date.now() : undefined;
    const isValid = currentTtl !== undefined && currentTtl > 0;
    setCanAutoConfirm(isValid);
  }, [enclaveSessionValidUntil]);

  useTimeout(() => {
    setCanAutoConfirm(false);
  }, autoConfirmTtl && autoConfirmTtl > 0 ? autoConfirmTtl : undefined);

  return !isDisabled && canAutoConfirm;
}

export default memo(withGlobal<OwnProps>((global): StateProps => {
  const { isPasswordNumeric, isAutoConfirmEnabled } = global.settings;
  const enclaveSessionValidUntil = global.enclaveSession?.validUntil;
  const isBiometricAuthEnabled = selectIsBiometricAuthEnabled(global);
  const shouldMigrate = selectShouldMigrate(global);
  const hasLegacyBiometrics = shouldMigrate ? selectHasLegacyBiometrics(global) : undefined;
  const legacyAuthConfig = shouldMigrate ? selectLegacyAuthConfig(global) : undefined;

  return {
    isPasswordNumeric,
    isAutoConfirmEnabled,
    enclaveSessionValidUntil,
    isBiometricAuthEnabled,
    shouldMigrate,
    hasLegacyBiometrics,
    legacyAuthConfig,
    authUsageCountRequest: selectAuthUsageCountRequest(global),
    multichainUpgradeAccountIds: global.multichainUpgradeAccountIds,
    isAppLocked: global.isAppLockActive,
  };
})(PasswordForm));
